import { Config } from '@shared/configs/config';
import { switchMap, tap } from 'rxjs/operators';
import { UntypedFormBuilder, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { ProjectCreatorApiService } from '../services/project-creator-api.service';
import { BaseProjectCreator } from './base-project-creator.controller';
import { EProjectCreatorThirdStepForm } from '../enums/project-creator-third-step.enum';
import { EBudgetType } from '../enums/budget-type.enum';
import { priceToPenny } from '@shared/helpers/price-to-penny.helper';
import { ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

export class ProjectCreatorThirdStepController extends BaseProjectCreator {
  get defaultFormValues() {
    return {
      [EProjectCreatorThirdStepForm.budgetType]:
        this.project?.basicDataBox?.budgetType || EBudgetType.BUDGET_TYPE_OVERALL,
      [EProjectCreatorThirdStepForm.budgetAmount]: this.project?.basicDataBox?.budgetAmount
        ? (Number(this.project?.basicDataBox?.budgetAmount) / 100).toFixed(2)
        : null,
      [EProjectCreatorThirdStepForm.termEnd]: this.project?.basicDataBox?.termEnd || null,
      [EProjectCreatorThirdStepForm.termStart]: this.project?.basicDataBox?.termStart || null,
      [EProjectCreatorThirdStepForm.termChangeComment]: null
    };
  }

  sub: Subscription = new Subscription();
  initStartDate: string;
  initEndDate: string;
  hideCommentInput = false;

  constructor(
    private fb: UntypedFormBuilder,
    private api: ProjectCreatorApiService
  ) {
    super();
    this.createForm(this.fb);
    this.setValidators();
  }

  setValidators() {
    this.form
      .get(EProjectCreatorThirdStepForm.budgetAmount)
      .setValidators([Validators.max(Config.MAX_NUMBER), this.validateNumber()]);
    this.form.get(EProjectCreatorThirdStepForm.budgetAmount).updateValueAndValidity();
  }

  subscribeChange(changes: ChangeDetectorRef): void {
    this.sub?.unsubscribe();
    this.sub = new Subscription();

    const sub = this.form.valueChanges.subscribe((val) => {
      const { termStart, termEnd, termChangeComment } = val;
      const initStart = this.initStartDate;
      const initEnd = this.initEndDate;

      const termChanged = initStart !== termStart || initEnd !== termEnd;
      const termUnchanged = initStart === termStart && initEnd === termEnd;
      const bothTermsExist = !!(termStart && termEnd);
      const bothInitsExist = !!(initStart && initEnd);
      const noInits = !initStart && !initEnd;

      this.hideCommentInput = !termChanged ? this.hideCommentInput : false;

      if ((termChanged && termChangeComment) || termUnchanged || (bothTermsExist && noInits)) {
        this.dataChanged = true;
      } else if ((!termStart || !termEnd) && bothInitsExist) {
        this.dataChanged = false;
      } else {
        this.dataChanged = false;
      }

      changes?.detectChanges();
    });

    this.sub.add(sub);
  }

  setFormValues(onlySelf: boolean = false) {
    this.initStartDate = this.defaultFormValues[EProjectCreatorThirdStepForm.termStart];
    this.initEndDate = this.defaultFormValues[EProjectCreatorThirdStepForm.termEnd];
    this.form?.setValue(this.defaultFormValues, onlySelf ? { onlySelf: true, emitEvent: false } : {});
  }

  validateNumber(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return isNaN(Number(control?.value?.toString() || '')) ? { isNaN: true } : null;
    };
  }

  setBudgetType(type: string) {
    this.form.get(EProjectCreatorThirdStepForm.budgetType).setValue(type);
  }

  submit(token: string, step?: number): any {
    const obj = Object.assign({}, this.form.value);
    obj[EProjectCreatorThirdStepForm.budgetAmount] = !!obj[EProjectCreatorThirdStepForm.budgetAmount]
      ? priceToPenny(obj[EProjectCreatorThirdStepForm.budgetAmount])
      : 0;
    return this.api.updateThirdStep(token, obj).pipe(switchMap(() => this.api.getFullProjectData(token)));
  }

  submitInternal(id: number, step?: number) {
    const obj = Object.assign({}, this.form.value);
    obj[EProjectCreatorThirdStepForm.budgetAmount] = !!obj[EProjectCreatorThirdStepForm.budgetAmount]
      ? priceToPenny(obj[EProjectCreatorThirdStepForm.budgetAmount])
      : 0;
    return this.api.updateThirdStepInternal(id, obj).pipe(
      tap(() => (this.hideCommentInput = true)),
      switchMap(() => this.api.getProjectById(id))
    );
  }
}
